import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CHIVO, Linkify, Page, Steps, money, useMobile, useSite } from "@/components/site/Chrome";

const H2 = ({ children, size = 22 }) => <h2 style={{ fontFamily: CHIVO, fontWeight: 700, fontSize: size, letterSpacing: "-0.02em" }}>{children}</h2>;

function SalesLead({ sales }) {
  if (!sales) return null;
  return (
    <div style={{ marginTop: 18 }} data-testid="cert-sales-lead">
      <p data-testid="cert-tagline" style={{ fontFamily: CHIVO, fontWeight: 700, fontSize: 20, lineHeight: 1.3, letterSpacing: "-0.02em" }}>{sales.tagline}</p>
      <p data-testid="cert-hero-copy" style={{ marginTop: 12, fontSize: 16.5, lineHeight: 1.6, color: "var(--ink-3)", maxWidth: "34em" }}>{sales.hero}</p>
    </div>
  );
}

function WhoFor({ text }) {
  if (!text) return null;
  return <div style={{ marginTop: 40 }} data-testid="cert-who-for"><H2 size={20}>Who it is for</H2><p style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.65, color: "var(--ink-3)", maxWidth: "40em" }}>{text}</p></div>;
}

function Qualify({ t }) {
  if (!t.qualify) return null;
  return (
    <div style={{ marginTop: 40 }} data-testid="cert-qualify">
      <H2 size={20}>How to qualify</H2>
      <p style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.65, color: "var(--ink-3)", maxWidth: "40em" }}>{t.qualify}</p>
      {t.certifying_body_url && <p style={{ marginTop: 8, fontSize: 14 }}><a href={t.certifying_body_url} data-testid="cert-body-link" rel="noreferrer">Requirements at {t.vendor || "the certifying body"}</a></p>}
    </div>
  );
}

function SeoSections({ sections }) {
  if (!sections?.length) return null;
  return (
    <div style={{ marginTop: 40 }} data-testid="cert-seo-sections">
      {sections.map((s, i) => (
        <div key={i} style={{ marginTop: i ? 32 : 0 }} data-testid={`cert-seo-section-${i + 1}`}>
          <H2 size={22}>{s.title}</H2>
          <p style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.7, color: "var(--ink-3)", maxWidth: "44em" }}>{s.body}</p>
        </div>
      ))}
    </div>
  );
}

function ExamFactsStrip({ t }) {
  const f = t.exam_facts;
  if (!f) return null;
  const cells = [["Questions", <><span className="dash">{f.question_count}</span> maximum</>], ["Time limit", <><span className="dash">{f.time_limit_min}</span> minutes</>],
    ["Passing score", <span className="dash">{f.passing_score}</span>], [f.format_label, <span className="dash">{f.question_formats.join(", ")}</span>, "vendor-format"],
    ["Objectives", <span className="dash">{f.objectives_version}</span>]];
  return (
    <div style={{ marginTop: 26, borderTop: "2px solid var(--teal)", background: "var(--surface)" }} data-testid="cert-exam-facts">
      <p className="label" style={{ padding: "12px 18px 0", color: "var(--ink-4)" }} data-testid="cert-exam-facts-heading"><span className="dash">{f.heading.toUpperCase()}</span></p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0 32px", padding: "10px 18px 14px" }}>
        {cells.map(([k, v, id]) => (
          <div key={k} style={{ padding: "6px 0" }} data-testid={`cert-fact-${id || k.toLowerCase().replace(/ /g, "-")}`}>
            <p style={{ fontSize: 12, color: "var(--ink-5)" }}>{k}</p>
            <p className="mono" style={{ marginTop: 3, fontSize: 14.5 }}>{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Details({ t }) {
  const cells = [["Exam code", <span className="dash">{t.exam_code}</span>], ["Questions", <><span className="dash">{t.questions_per_exam}</span> maximum</>],
    ["Time limit", <><span className="dash">{t.time_limit_min}</span> minutes</>], ["Passing score", <><span className="dash">{t.pass_mark_pct}</span>%</>],
    ["Practice exam format", <span className="dash">{t.format}</span>], ["Delivery", <span className="dash">{t.delivery}</span>]];
  return (
    <div style={{ marginTop: 34, border: "1px solid #DDE1E6" }} data-testid="cert-details">
      <p className="label" style={{ padding: "12px 18px", borderBottom: "1px solid var(--hair)", background: "var(--surface)", color: "var(--ink-4)" }}>{t.exam_facts ? "OUR PRACTICE EXAMS" : "EXAM DETAILS"}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
        {cells.map(([k, v], i) => (
          <div key={k} style={{ padding: "16px 18px", borderBottom: i < 4 ? "1px solid var(--hair-2)" : undefined, borderRight: i % 2 === 0 ? "1px solid var(--hair-2)" : undefined }}>
            <p style={{ fontSize: 12.5, color: "var(--ink-5)" }}>{k}</p>
            <p className="mono" style={{ marginTop: 5, fontSize: 15 }}>{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Outline({ t }) {
  const max = Math.max(...t.domains.map((d) => d.weight || 0), 1);
  return (
    <div style={{ marginTop: 40 }} data-testid="cert-outline">
      <H2 size={24}>Exam content outline</H2>
      <p style={{ marginTop: 8, fontSize: 14, color: "var(--ink-4)" }}>{t.domains.length} domains. Our exams draw questions in the same proportion.</p>
      <div className="steps" style={{ marginTop: 20, display: "flex", flexDirection: "column" }}>
        {t.domains.map((d) => (
          <div key={d.id} style={{ padding: "16px 0", display: "grid", gridTemplateColumns: "minmax(0,1fr) 88px 52px", gap: 16, alignItems: "center" }}>
            <div>
              <p style={{ fontWeight: 600, fontSize: 15 }}><span className="mono" style={{ color: "var(--ink-5)", marginRight: 10 }}>{d.id}</span>{d.name}</p>
              {d.blurb && <p style={{ marginTop: 5, fontSize: 13.5, color: "var(--ink-4)" }}>{d.blurb}</p>}
            </div>
            <div className="bar"><i style={{ width: `${Math.round((d.weight || 0) / max * 100)}%` }} /></div>
            <p className="mono" style={{ fontSize: 14, textAlign: "right" }}>{d.weight}%</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FreeBlock({ t, slug }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState(null);
  const send = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/free/start", { cert_slug: slug, email: email.trim().toLowerCase() }); setState(data); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div style={{ marginTop: 40, border: "1px solid #DDE1E6", background: "var(--surface)", padding: 28 }} data-testid="cert-free-block">
      <H2>Try <span className="dash">{t.free_count}</span> <span className="dash">{t.cert_short}</span> questions free</H2>
      <p style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.6, color: "var(--ink-3)", maxWidth: "40em" }}>Drawn across all {t.domains.length} domains, scored with the same domain breakdown as the paid exams. Email → magic link → start.</p>
      {state?.sent ? (
        <p data-testid="cert-free-sent" style={{ marginTop: 18, fontSize: 14.5, color: "var(--ac-deep)" }}>Check your inbox — your link is on its way.{state.dev_magic_link && <> <a href={state.dev_magic_link} data-testid="cert-free-dev-link">Open it now (dev)</a></>}</p>
      ) : (
        <form onSubmit={send} style={{ marginTop: 18, display: "flex", flexWrap: "wrap", gap: 10 }}>
          <input data-testid="cert-free-email" type="email" required placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} style={{ flex: "1 1 240px", padding: "12px 14px", border: "1px solid var(--field)", borderRadius: 4, fontSize: 15 }} />
          <button data-testid="cert-free-submit" className="btn dark" style={{ padding: "12px 24px" }}>Send my free exam</button>
        </form>
      )}
    </div>
  );
}

function BuyBox({ t, slug, also, sales, image }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const buy = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { cert_slug: slug, email: email.trim().toLowerCase(), origin_url: window.location.origin, ...(code.trim() && { discount_code: code.trim() }) });
      window.location.href = data.checkout_url || data.url;
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <aside style={{ position: image ? "static" : "sticky", top: 76, width: "100%", maxWidth: 400 }} data-testid="cert-buy-box">
      {image && (
        <img src={image.url} alt={`${t.cert_name} practice exams`} width={image.width || 752} height={image.height || 1536} data-testid="cert-course-image"
             style={{ display: "block", width: "100%", height: "auto", marginBottom: 16, border: "1px solid var(--hair)" }} />
      )}
      <form onSubmit={buy} style={{ border: "1px solid var(--ac)" }}>
        <div className="er-rule" />
        <div style={{ padding: "26px 24px" }}>
          <p style={{ display: "flex", alignItems: "baseline", gap: 8 }}><span className="mono" style={{ fontSize: 34, letterSpacing: "-0.02em" }} data-testid="buy-price">{money(t.price)}</span><span style={{ fontSize: 13.5, color: "var(--ink-4)" }}>once · <span className="dash">{t.access_months}</span> months access</span></p>
          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10, fontSize: 14 }}>
            {(sales?.bullets || [<><span className="dash">{t.exams_per_cert}</span> full-length exams, <span className="dash">{t.bank_total}</span> unique questions</>, "Explanations for all four options", "Domain scoring and weakest-domain report", `Free updates if ${t.exam_code || t.cert_short} is revised`, "Pass guarantee"]).map((x, i) => (
              <p key={i} style={{ display: "flex", gap: 10 }}><span style={{ color: "var(--ac)" }}>—</span><span>{x}</span></p>
            ))}
          </div>
          <label className="field-label" style={{ marginTop: 22 }}>EMAIL FOR ACCESS</label>
          <input data-testid="buy-email" className="field" type="email" required placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} style={{ padding: "11px 13px", fontSize: 14.5 }} />
          <label className="field-label" style={{ marginTop: 12 }}>DISCOUNT CODE (OPTIONAL)</label>
          <input data-testid="buy-code" className="field mono" placeholder="CODE" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} style={{ padding: "11px 13px" }} />
          <button data-testid="buy-submit" disabled={busy} className="btn primary block" style={{ marginTop: 16 }}>{busy ? "Opening checkout…" : "Get instant access"}</button>
          <p style={{ marginTop: 12, textAlign: "center", fontSize: 12, color: "var(--ink-5)" }}>Card handled by Stripe. Access opens immediately.</p>
          {sales?.guarantee_line && <p data-testid="cert-guarantee-line" style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.5, color: "var(--ink-4)" }}><Linkify text={sales.guarantee_line} /></p>}
        </div>
      </form>
      {also.length > 0 && (
        <div style={{ marginTop: 16, border: "1px solid var(--hair)", padding: "18px 20px" }} data-testid="also-available">
          <p className="label">ALSO AVAILABLE</p>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 9, fontSize: 14, alignItems: "flex-start" }}>
            {also.map((o) => <Link key={o.slug} className="nav-link" data-testid={`also-${o.slug}`} to={`/${o.slug}`}>{o.short} · {money(o.price_usd)}</Link>)}
          </div>
        </div>
      )}
    </aside>
  );
}

export default function CertLanding() {
  const { slug } = useParams();
  const mob = useMobile();
  const site = useSite();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { setPage(null); setMissing(false); api.get(`/landing/${slug}`).then((r) => setPage(r.data)).catch(() => setMissing(true)); }, [slug]);
  useEffect(() => {
    if (!page) return;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    if (page.page?.canonical) link.href = page.page.canonical;
    const blocks = [["ld-product", page.landing?.schema], ["ld-faq", page.landing?.faq_schema]];
    blocks.forEach(([id, data]) => {
      document.getElementById(id)?.remove();
      if (!data) return;
      const s = document.createElement("script"); s.type = "application/ld+json"; s.id = id; s.text = JSON.stringify(data); document.head.appendChild(s);
    });
    return () => blocks.forEach(([id]) => document.getElementById(id)?.remove());
  }, [page]);
  const t = page?.tokens;
  const g = site?.guarantee;
  const sales = page?.sales_page || null;
  return (
    <Page title={page?.page?.seo_title || "ExamRay"} description={page?.page?.meta_description || t?.summary} testid="cert-landing">
      {missing && <section style={{ padding: "80px 32px" }} data-testid="cert-missing"><H2 size={30}>This certification is not published.</H2><p style={{ marginTop: 12 }}><Link to="/">Back to the catalog</Link></p></section>}
      {t && (
        <>
          <div className="mono" style={{ padding: mob ? "20px 20px 0" : "20px 32px 0", fontSize: 11, letterSpacing: "0.06em", color: "var(--ink-5)" }}>examray.com/<span className="dash">{t.cert_slug}</span></div>
          <section style={mob ? { padding: "26px 20px 48px", display: "flex", flexDirection: "column", gap: 36 } : { padding: "26px 32px 48px", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(380px,1fr))", gap: 44, alignItems: "start" }}>
            <div>
              <p className="kicker deep" data-testid="cert-kicker"><span className="dash">{t.vendor.toUpperCase()}</span> · <span className="dash">{t.exam_code}</span> · <span className="dash">{t.version ? `${t.version.toUpperCase()} · CURRENT VERSION` : "CURRENT VERSION"}</span></p>
              <h1 data-testid="cert-h1" style={{ marginTop: 14, fontFamily: CHIVO, fontWeight: 900, fontSize: 44, lineHeight: 1.06, letterSpacing: "-0.03em" }}><span className="dash">{t.cert_name}</span> practice exams</h1>
              {sales ? <SalesLead sales={sales} /> : <p data-testid="cert-summary" style={{ marginTop: 18, fontSize: 16.5, lineHeight: 1.6, color: "var(--ink-3)", maxWidth: "34em" }}>{t.summary}</p>}
              <ExamFactsStrip t={t} />
              <Details t={t} />
              <Outline t={t} />
              <WhoFor text={sales?.who_for} />
              <Qualify t={t} />
              <FreeBlock t={t} slug={slug} />
              <div style={{ marginTop: 40 }} data-testid="cert-guarantee">
                <H2>Pass guarantee, in plain English</H2>
                <div style={{ marginTop: 16 }}><Steps items={t.guarantee_terms} testid="cert-guarantee-band" /></div>
              </div>
              {page.landing?.faq?.length > 0 && (
                <div style={{ marginTop: 40 }} data-testid="cert-faq">
                  <H2>Questions</H2>
                  <div className="steps" style={{ marginTop: 16, display: "flex", flexDirection: "column" }}>
                    {page.landing.faq.map((f, i) => (
                      <div key={i} style={{ padding: "14px 0" }}><p style={{ fontWeight: 600, fontSize: 15 }}>{f.q}</p><p style={{ marginTop: 6, fontSize: 14.5, lineHeight: 1.6, color: "var(--ink-3)" }}>{f.a}</p></div>
                    ))}
                  </div>
                </div>
              )}
              <SeoSections sections={page.seo_sections} />
            </div>
            <BuyBox t={t} slug={slug} also={page.also_available || []} sales={sales} image={page.course_image} />
          </section>
          {g && <span style={{ display: "none" }} data-testid="cert-guarantee-pct">{g.pass_pct}</span>}
        </>
      )}
    </Page>
  );
}
